/**
 * Music. Drop MP3s into `public/music/` — no manifest needed:
 *  - `track_1.mp3`, `track_2.mp3`, ... — level music (levels rotate through them)
 *  - `menu_1.mp3` — loops on the menu / lobby / end screens
 */
const MAX_TRACKS = 12;

export class MusicPlayer {
  private tracks: string[] = [];
  private menuTracks: string[] = [];
  private current: HTMLAudioElement | null = null;
  private discovered: Promise<void>;
  /** What should be playing right now — resumed on unmute. */
  private desired: 'menu' | number | null = null;
  private mutedFlag: boolean;

  constructor(private volume = 0.35) {
    this.discovered = this.discover();
    this.mutedFlag = localStorage.getItem('gp-music-muted') === '1';
  }

  get muted(): boolean {
    return this.mutedFlag;
  }

  /** Flip mute; resumes whatever should be playing on unmute. Returns new state. */
  toggleMuted(): boolean {
    this.mutedFlag = !this.mutedFlag;
    localStorage.setItem('gp-music-muted', this.mutedFlag ? '1' : '0');
    if (this.mutedFlag) {
      this.stop();
    } else if (this.desired === 'menu') {
      this.playMenu();
    } else if (typeof this.desired === 'number') {
      this.playForLevel(this.desired);
    }
    return this.mutedFlag;
  }

  private async discover(): Promise<void> {
    const head = async (url: string) => {
      try {
        const res = await fetch(url, { method: 'HEAD' });
        return res.ok ? url : null;
      } catch {
        return null;
      }
    };
    const levelChecks = Array.from({ length: MAX_TRACKS }, (_, i) => head(`/music/track_${i + 1}.mp3`));
    const menuChecks = Array.from({ length: 4 }, (_, i) => head(`/music/menu_${i + 1}.mp3`));
    this.tracks = (await Promise.all(levelChecks)).filter((u): u is string => u !== null);
    this.menuTracks = (await Promise.all(menuChecks)).filter((u): u is string => u !== null);
    if (this.tracks.length + this.menuTracks.length > 0) {
      console.log(`[Music] ${this.tracks.length} level + ${this.menuTracks.length} menu track(s) found`);
    }
  }

  /** Start (or switch to) the track for this level. Loops until stop(). */
  async playForLevel(level: number): Promise<void> {
    this.desired = level;
    await this.discovered;
    if (this.tracks.length === 0 || this.mutedFlag) return;
    this.play(this.tracks[(Math.max(1, level) - 1) % this.tracks.length]);
  }

  /** Menu / lobby / end-screen music (fades out level music either way). */
  async playMenu(): Promise<void> {
    this.desired = 'menu';
    await this.discovered;
    if (this.menuTracks.length === 0 || this.mutedFlag) {
      this.stop();
      return;
    }
    this.play(this.menuTracks[0]);
  }

  private play(url: string): void {
    if (this.current && this.current.src.endsWith(url) && !this.current.paused) {
      return; // already playing
    }
    this.stop();
    const audio = new Audio(url);
    audio.loop = true;
    audio.volume = this.volume;
    this.current = audio;
    // autoplay policies can reject before a user gesture — that's fine
    audio.play().catch(() => {});
  }

  /** Fade out and stop. */
  stop(): void {
    const audio = this.current;
    if (!audio) return;
    this.current = null;

    const fade = setInterval(() => {
      if (audio.volume > 0.03) {
        audio.volume = Math.max(0, audio.volume - 0.03);
      } else {
        clearInterval(fade);
        audio.pause();
        audio.src = '';
      }
    }, 60);
  }
}
